import type { LearningsAskStreamEvent, LearningsAskTurn } from 'definitions';
import type { UserSummary } from 'pertentodb/types';
import type { ResultsTurn } from '@/state/results-ai/results-ai-thread';
import { applyEvent, newTurn } from '@/state/results-ai/results-ai-thread';

/**
 * The learnings Ask's conversation as the panel shows it, built on the results chat's (the two
 * stream the same events, results-ai-thread.ts): a turn is a results turn, plus the website's tests
 * its answer names, checked by the server once the turn is stored, and how many tests have been
 * read since.
 */
export type AskTurnView = ResultsTurn & {
  /** Empty until the stored turn arrives: only then are the answer's #ids checked and linked. */
  citations: number[];
  testsReadSince: number;
};

type NewAskTurnParams = { key: string; question: string };

export const newAskTurn = ({ key, question }: NewAskTurnParams): AskTurnView => ({
  ...newTurn({ key, question }),
  citations: [],
  testsReadSince: 0,
});

/** One streamed event applied to the turn, as the results chat applies it. Never mutates. */
export const applyAskEvent = (turn: Readonly<AskTurnView>, event: Readonly<LearningsAskStreamEvent>): AskTurnView => ({
  ...turn,
  ...applyEvent(turn, event),
});

const STORED_PHASE: Record<LearningsAskTurn['status'], ResultsTurn['phase']> = {
  completed: 'done',
  errored: 'errored',
  aborted: 'stopped',
};

type FromStoredParams = { turn: Readonly<LearningsAskTurn>; authors: Readonly<Record<string, UserSummary>> };

/** A stored turn as the panel shows it; a completed turn with an error message is a refusal. */
export const fromStoredTurn = ({ turn, authors }: FromStoredParams): AskTurnView => ({
  key: turn.turnId,
  turnId: turn.turnId,
  question: turn.question,
  answer: turn.answer,
  steps: [],
  phase: turn.errorMessage && turn.status === 'completed' ? 'errored' : STORED_PHASE[turn.status],
  ...(turn.errorMessage ? { error: { message: turn.errorMessage, retryable: turn.errorRetryable } } : {}),
  ...(authors[String(turn.senderId)] ? { sender: authors[String(turn.senderId)] } : {}),
  credits: turn.credits,
  citations: turn.citations,
  testsReadSince: turn.testsReadSince,
});

/** "4 tests have been read since this answer.", or null when none have. */
export const staleLine = (testsReadSince: number): string | null =>
  testsReadSince > 0
    ? `${testsReadSince} ${testsReadSince === 1 ? 'test has' : 'tests have'} been read since this answer.`
    : null;
